import React, { useCallback, useEffect, useState, useRef } from "react";
import styles from "../styles/universities.module.scss";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import fetchUtil from "../utils/fetchUtil";
import _debounce from "lodash/debounce";
import Select from "react-select";
import { useRouter } from "next/router";
import { unicountries } from "../utils/constants";
import { getCountryFromSlug, getSlugFromCountryUni, getSlugFromCountryUniDetail, isFrappeOrJotFormClass } from "../utils/helpers";
import CustomImage from "./CustomImage";
import Button from "react-bootstrap/Button";
import NeedGuidance from "./NeedGuidance";
import Link from "next/dist/client/link";
import StudiesFAQ from "./AdmissionsDay/StudiesFAQ";
import CountryFAQ from "./CountryFAQ";
import { loadJotFormScript } from "../utils/loadJotFormScript";
import { getOnboardingOrFormCtaProps } from "../utils/applyCtaButtonProps";
import { persistSourceContext } from "../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../utils/b2c/constants";
export interface FilterProps {
  country: string;
  searchValue: string;
  pageNo: number;
}

let mounted = false;
const Universities: React.FC = (props: any) => {
  const router = useRouter();
  const universityData = props?.pageData;
  const jotformScriptLoaded = useRef(false);
  const { countrySlug, pageNumber, countryFaqsData } = props;
  const countryFromSlug = props.countrySlug && getCountryFromSlug(props.countrySlug, unicountries);

  const initialValues = {
    country: countryFromSlug?.replace(/ /g, ""),
    searchValue: "",
    pageNo: pageNumber ? parseInt((pageNumber as string)?.substring(5)) : 1,
  };

  const [filters, setFilters] = useState<FilterProps>(initialValues);
  const [universities, setUniversities] = useState<any>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<any>({});
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { country, searchValue, pageNo } = filters;
  const [paginateKey, setPaginateKey] = useState(0);

  const debounceSearchQuery = useCallback((searchKeyValue) => {
    const search = _debounce(
      () =>
        setFilters((prevFilters) => ({
          ...prevFilters,
          searchValue: searchKeyValue,
        })),
      500,
    );

    setSearchQuery((prevSearch: { cancel: () => void }) => {
      if (prevSearch && prevSearch.cancel) {
        prevSearch.cancel();
      }
      return search;
    });

    search();
  }, []);

  const formInstance = useRef<any>(null);
  useEffect(() => {
    loadJotFormScript(() => {
      jotformScriptLoaded.current = true;
    });
  }, []);

  const handleSearchChange = (e: any) => {
    mounted = false;
    const searchValues = e.target.value;
    setSearchKeyword(searchValues);
    debounceSearchQuery(searchValues);
  };

  useEffect(() => {
    if (mounted) return;

    const url = searchValue ? `&name_contains=${searchValue}` : "";
    const countryParam = countryFromSlug ? `&country=${countryFromSlug?.replace(/ /g, "")}` : "";

    const fetchData = async () => {
      const [universities, count] = await Promise.all([
        fetchUtil(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/universities?_sort=priority:ASC,created_at:asc${countryParam}&_start=${(pageNo - 1) * 28}&_limit=28${url}`,
          "GET",
          {},
          "",
          false,
        ),
        fetchUtil(`${process.env.NEXT_PUBLIC_API_BASE_URL}/universities/count?${countryParam}${url}`, "GET", {}, "", false),
      ]);
      setUniversities(universities);
      setTotalPages(Math.ceil((count as number) / 28));
      setTotalCount(count as number);
    };

    fetchData().then(() => {
      mounted = true;
    });
  }, [searchValue, pageNo, countryFromSlug]);

  const selectOptions =
    props?.universityCountries?.length > 0
      ? [
          { label: "All", value: "all" },
          ...props.universityCountries.map((coun: any) => {
            return { label: coun.label, value: coun.label };
          }),
        ]
      : [{ label: "All", value: "all" }];

  const pageRangeDisplayed = 2; // How many pages to show around the current page
  const marginPagesDisplayed = 1; // Pages at the beginning and end to always show

  const handlePageClick = useCallback(
    (page: number) => {
      mounted = false;
      setFilters((prevFilters) => ({
        ...prevFilters,
        pageNo: page,
      }));

      const basePath = countryFromSlug ? `/universities/${countryFromSlug.toLowerCase().replace(/ /g, "-")}` : "/universities";

      const newPath = page === 1 ? basePath : `${basePath}/page-${page}`;

      router.push(newPath);
    },
    [countryFromSlug, router],
  );

  // Function to generate href based on page number and country
  const getHrefForPage = useCallback(
    (page: any) => {
      const basePath = countryFromSlug ? `/universities/${countryFromSlug.toLowerCase().replace(/ /g, "-")}` : `/universities`;
      return page === 1 ? basePath : `${basePath}/page-${page}`;
    },
    [countryFromSlug],
  );

  // Function to determine which pages to show (including ellipses)
  const getPagesToDisplay = useCallback(() => {
    let pages = [];

    // Always show the first page
    if (pageNo > pageRangeDisplayed + marginPagesDisplayed + 1) {
      pages.push(1);
      pages.push("...");
    }

    // Pages around the current page
    for (let i = Math.max(1, pageNo - pageRangeDisplayed); i <= Math.min(totalPages, pageNo + pageRangeDisplayed); i++) {
      pages.push(i);
    }

    // Always show the last page
    if (pageNo < totalPages - pageRangeDisplayed - marginPagesDisplayed) {
      pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  }, [pageNo, totalPages, pageRangeDisplayed, marginPagesDisplayed]);

  const pagesToDisplay = getPagesToDisplay();

  //String Manipulation Function
  const addSpaceToCountryName = useCallback((country: any) => {
    return country.replace(/([a-z])([A-Z])/g, "$1 $2");
  }, []);
  const isWorking = true;
  const formattedCountry = countryFromSlug ? `${isWorking ? "in" : ""} ${addSpaceToCountryName(countryFromSlug)}` : "To Study Abroad";

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: universityData?.bannerBtn?.btnLink,
    formId: universityData?.bannerBtn?.formId,
    toOnboardingEnabled: props?.pageData?.bannerBtn?.toOnboardingEnabled,
  });

  const enquireNowClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyCtaButtonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: universityData?.bannerBtn?.onBoardingFlow?.nearestOffice ?? true,
        coachingVisible: universityData?.bannerBtn?.onBoardingFlow?.lookingForCoaching ?? true,
      });
    }
    applyCtaButtonProps.onClick?.(e);
  };

  const applyClickHandler = (applyNowButtonProps: any, receivedData: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyNowButtonProps?.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: receivedData?.onBoardingFlow?.nearestOffice ?? true,
        coachingVisible: receivedData?.onBoardingFlow?.lookingForCoaching ?? true,
      });
    }

    applyNowButtonProps?.onClick?.(e);
  };

  return (
    <>
      <div id="universiti" className={styles.edtech}>
        <Container>
          <div className={styles.breadcrum}>
            <Row className="text-center">
              <Col lg={8} md={10} xs={12} className={styles.centered}>
                <Breadcrumb>
                  <Breadcrumb.Item style={{ opacity: 0.5 }} href="/">
                    Home
                  </Breadcrumb.Item>
                  <Breadcrumb.Item href=""> Universities </Breadcrumb.Item>
                </Breadcrumb>
                <div className={styles.abttopContent}>
                  <div className={styles.abttopContentdesk}>
                    <h1> List of Top Universities {formattedCountry}</h1>
                    <p>{props?.pageData?.description}</p>
                  </div>
                  <Button
                    id="enquirebtn"
                    variant="primary"
                    size="lg"
                    onClick={enquireNowClickHandler}
                    className={`${styles.enquirebtn} ${applyCtaButtonProps.formClassName}`}
                  >
                    {props?.pageData?.bannerBtn?.label || "Enquire Now"}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <div id="search-study-des" className={styles.searchStudyDesti}>
        <div className={styles.searchPan}>
          <div className={styles.searchBox}>
            <h2 className="text-center">Select Country</h2>
            <div className={styles.subscribeForm}>
              <div>
                <Select
                  options={selectOptions}
                  placeholder="All"
                  isClearable={false}
                  className={styles.selectClass}
                  aria-label="Select country"
                  classNamePrefix="mySelect"
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  value={
                    countryFromSlug && {
                      label: countryFromSlug,
                      value: countryFromSlug,
                    }
                  }
                  onChange={(selected: any) => {
                    mounted = false;
                    setPaginateKey(paginateKey + 1);
                    setFilters({
                      ...filters,
                      country: selected.value,
                      pageNo: 1,
                    });

                    const newPath = selected.label === "All" ? `/universities` : `/universities/${getSlugFromCountryUni(selected.value, unicountries)}`;
                    router.push(newPath);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="university-resu" className={styles.universityResults}>
        <Container>
          <Row className={styles.showResult}>
            <Col lg={9} md={8} xs={12}>
              <p>Showing Results in{" "}{countryFromSlug ? countryFromSlug : "All Countries"}</p>
            </Col>
            <Col lg={3} md={4} xs={12}>
              <form className="navbar-form" role="search" onSubmit={(e) => e.preventDefault()}>
                <div className="input-group add-on">
                  <div className={styles.inputBox}>
                    <input
                      className="form-control"
                      placeholder="Search Universities"
                      type="text"
                      value={searchKeyword}
                      onChange={handleSearchChange}
                      aria-label="Search universities"
                      name="search-universities"
                    />
                    <div className={styles.inputBtn}>
                      <CustomImage src="/searchco.svg" alt="searchco" width={14} height={14} />
                    </div>
                  </div>
                </div>
              </form>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} xs={12}>
              <Row>
                {universities?.length > 0 &&
                  universities.map((data: any, index: number) => {
                    const slug = data.slug;
                    const url = slug && data.country ? `/universities/${getSlugFromCountryUniDetail(data.country, unicountries)}/${slug}` : "";

                    const applyNowButtonProps = getOnboardingOrFormCtaProps(router, {
                      btnLink: data?.card?.button2?.btnLink,
                      formId: data?.card?.button2?.formId,
                      toOnboardingEnabled: data?.card?.button2?.toOnboardingEnabled,
                    });
                    return slug ? (
                      <Col xxl={3} xl={3} lg={4} md={4} sm={6} xs={12}>
                        <div key={index} className={`${styles.univerResult} ${url ? `${styles.activeUrl}` : `${styles.deactiveUrl}`}`}>
                          <div className={`${styles.uniLogo} p-0`}>
                            <CustomImage
                              src={data?.universityLogo?.imageUrl || "/images/university-detail-gallery/university-dummy-logo.jpg"}
                              alt={data?.image?.alt || data?.name}
                              width={261}
                              height={118}
                            />
                          </div>
                          <div className={styles.alignmentText}>
                            <h4>{data?.name}</h4>
                            {data?.card?.description && <h3 className={styles['card-desc']}>{data?.card?.description}</h3>}
                            {data?.location && <h3>{data?.location}</h3>}
                            <span>{data?.universityUrl}</span>
                          </div>
                          <div className={styles.footerButton}>
                            <Button onClick={() => window.open(url, "_blank")} className={styles.knowMore}>
                              {data?.card?.button1?.title ?? "Know More"}
                            </Button>
                            <Button
                              className={`${styles.applyNow} ${applyNowButtonProps.formClassName}`}
                              onClick={applyClickHandler(applyNowButtonProps, data?.card?.button2)}
                            >
                              {data?.card?.button2?.label ?? "Apply Now"}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    ) : (
                      <Col xxl={3} xl={3} lg={4} md={4} sm={6} xs={12}>
                        <div key={index} className={`${styles.univerResult} ${styles.deactiveUrl}`}>
                          <div className={`${styles.uniLogo} p-0`}>
                            <CustomImage
                              src={data?.universityLogo?.imageUrl || "/images/university-detail-gallery/university-dummy-logo.jpg"}
                              alt={data?.image?.alt}
                              width={261}
                              height={118}
                            />
                          </div>
                          <div className={styles.alignmentText}>
                            <h4>{data?.name}</h4>
                            {data?.card?.description && <h3 className={styles['card-desc']}>{data?.card?.description}</h3>}
                            {data?.location && <h3>{data?.location}</h3>}
                            <span>{data?.universityUrl}</span>
                            {/* <div dangerouslySetInnerHTML={{ __html: data?.description }}></div> */}
                          </div>
                          <div className={styles.footerButton}>
                            <Button
                              className={`${styles.applyNow} ${applyNowButtonProps.formClassName}`}
                              onClick={applyClickHandler(applyNowButtonProps, data?.card?.button2)}
                            >
                              {data?.card?.button2?.label ?? "Apply Now"}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                <ul className={styles.pagination}>
                  {pageNo > 1 && (
                    <li className="previous">
                      <Link href={getHrefForPage(pageNo - 1)}>
                        <a onClick={() => handlePageClick(pageNo - 1)} aria-label="Previous page" rel="prev">
                          &lt;
                        </a>
                      </Link>
                    </li>
                  )}

                  {pagesToDisplay.map((page, index) => (
                    <li key={index} className={pageNo === page ? styles.active : ""}>
                      {page === "..." ? (
                        <span className="ellipsis">...</span>
                      ) : (
                        <Link href={getHrefForPage(page)}>
                          <a onClick={() => handlePageClick(Number(page))} aria-label={`Page ${page}`} rel={pageNo === page ? "canonical" : "next"}>
                            {page}
                          </a>
                        </Link>
                      )}
                    </li>
                  ))}
                  {pageNo < totalPages && (
                    <li className="next">
                      <Link href={getHrefForPage(pageNo + 1)}>
                        <a onClick={() => handlePageClick(pageNo + 1)} aria-label="Next page" rel="next">
                          &gt;
                        </a>
                      </Link>
                    </li>
                  )}
                </ul>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>

      <div>
        <NeedGuidance classname={`${isFrappeOrJotFormClass(props?.pageData?.meta?.jotforms[0]?.jotformid || process.env.NEXT_PUBLIC_COMMON_JOTFORM_ID)}`} />
      </div>

      {router.pathname === "/universities" && props?.pageData?.faqSection && (
        <div>
          <StudiesFAQ faqdata={props?.pageData?.faqSection} />
        </div>
      )}

      {!(router.pathname === "/universities") &&
        countryFromSlug &&
        pageNo == 1 &&
        props?.countryFaqsData?.length > 0 &&
        props.countryFaqsData.some(
          (item: any) => item.countriesFaq?.university_country?.label === countryFromSlug || item.countriesFaq?.university_country?.value === countryFromSlug,
        ) && (
          <div className={styles.countryFaq} style={{ backgroundColor: "#FFFFFF" }}>
            <Container>
              <Row>
                <div className={styles.heading}>
                  <h2> Frequently Asked Questions about {countryFromSlug} Universities</h2>
                </div>
              </Row>
            </Container>

            <CountryFAQ
              faqdata={
                props?.countryFaqsData.find(
                  (item: any) =>
                    item.countriesFaq?.university_country?.label === countryFromSlug || item.countriesFaq?.university_country?.value === countryFromSlug,
                )?.countriesFaq?.faq
              }
            />
          </div>
        )}
    </>
  );
};

export default Universities;
